import * as formik from 'formik';
import * as yup from 'yup';
import { useState, useMemo, useEffect } from 'react';
import { Modal, Button, Table, Form, Row, Col, ListGroup } from 'react-bootstrap';
import { getTransaction, updateTransactionStatus } from '../../utils/api';
import Loader from '../Loader';
import Header from '../Header';
import Success from '../Success'
import Error from '../Error'
import SeperatorLine from '../SeparatorLine'
import { faCirclePlus, faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatCurrency, formatDateToMMDDYYYY, formatDateToYYYYMMDD, tableSort } from '../../utils/helpers';
import { isCancel } from 'axios';

const ViewTransactionModal = ({ show, onHide, transaction, transactionStatus }) => {

    const [data, setData] = useState(transaction);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isSaved, setIsSaved] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        const data = await getTransaction(transaction.Id);
        if (data) {
            setData(data);
        }
        else {
            setError('Failed to fetch data');
        }
        setLoading(false);
    };

    const resetState = () => {
        setData([]);
        setError(null);
        setLoading(false);
    };

    const closeModal = (shouldRefresh) => {
        setLoading(true);
        setIsSaving(false);
        setError(false);
        setIsSaved(false);
        onHide(shouldRefresh);
    }

    useEffect(() => {
        const onLoad = () => {
            if (show)
                fetchData();
            else
                resetState();
        }
        onLoad();
    }, [show]);

    const { Formik } = formik;
    const schema = yup.object().shape({
        confirmCheckbox: yup.bool().oneOf([true], 'You must confirm before continuing')
    });

    var buttonText = "View";
    if (transactionStatus === 2)
        buttonText = 'Confirm';
    else if (transactionStatus === 3)
        buttonText = 'Approve';
    else if (transactionStatus === 4)
        buttonText = 'Cancel';

    var confirmationText = "";
    if (transactionStatus === 2)
        confirmationText = 'I confirm that all information provided is accurate and understand that it will be used to complete the title application for this transaction.';
    else if (transactionStatus === 3)
        confirmationText = 'I confirm that all information provided is accurate and understand that no further action can be taken on this transaction.';
    else if (transactionStatus === 4)
        confirmationText = 'I confirm that this transaction will be cancelled and that no further action can be taken on this transaction.';

    return (
        <Modal show={show} onHide={onHide} backdrop={true} style={{ zIndex: 1051 }} dialogClassName="modal-90w" centered>
            <Modal.Header closeButton>
                <Modal.Title>{buttonText} Transaction (Id={transaction?.Id})</Modal.Title>
            </Modal.Header>

            {loading || isSaving ? (
                <div style={{ height: 230, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Loader showText={true} text={isSaving && 'Saving...'} />
                </div>
            ) : (
                error ? (
                    <div style={{ height: 230, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Error />
                    </div>
                ) : isSaved ? (
                    <div style={{ height: 230, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Success />
                    </div>
                ) : (
                    <Modal.Body>
                        <div style={{ padding: 10 }}>
                            <Header description={'Summary of the transaction details before submission. Please review all sections carefully, including but not limited to customer information, vehicle and asset details, lien holders, trade-ins, fees, and terms of the lease.'} />
                            <b>Transaction Details</b>
                            <Row className='mt-1'>
                                <Table striped bordered hover responsive>
                                    <thead>
                                        <tr>
                                            <th style={{ whiteSpace: 'nowrap' }}>Id</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Created</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Transaction Type</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Primary Usage</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Service Fee</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Rebate</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Titling State</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Status</th>
                                            <th style={{ whiteSpace: 'nowrap' }}>Confirmation Date </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{data?.Transaction?.Id}</td>
                                            <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{formatDateToMMDDYYYY(data?.Transaction?.CreatedDateTime)}</td>
                                            <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{data?.Transaction?.TransactionType?.Description}</td>
                                            <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{data?.Transaction?.AssetUsage?.Description}</td>
                                            <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{formatCurrency(data?.Transaction?.ServiceFee)}</td>
                                            <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{formatCurrency(data?.Transaction?.Rebate)}</td>
                                            <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{data?.Transaction?.TitlingState?.Name}</td>
                                            <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{data?.Transaction?.TransactionStatus?.Description}</td>
                                            <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{formatDateToMMDDYYYY(data?.Transaction?.ConfirmationDateTime)}</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </Row>
                            <br />
                            <b>Garaged Address</b>
                            <Row className='mt-1'>
                                {data?.Transaction?.GaragedAddress ? (
                                    <Table striped bordered hover responsive>
                                        <thead>
                                            <tr>
                                                <th style={{ whiteSpace: 'nowrap' }}>Id</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>Address</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>Address2</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>City</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>State</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>Zip</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{data?.Transaction?.GaragedAddress?.Id}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{data?.Transaction?.GaragedAddress?.AddressLine1}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{data?.Transaction?.GaragedAddress?.AddressLine2}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{data?.Transaction?.GaragedAddress?.City}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{data?.Transaction?.GaragedAddress?.State}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{data?.Transaction?.GaragedAddress?.PostalCode}</td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                ) : (<p>None</p>)}
                            </Row>
                            <br />
                            <b>Asset(s)</b>
                            <Row className='mt-1'>
                                {data?.Transaction?.TransactionAssets?.length > 0 ? (
                                    <Table striped bordered hover responsive>
                                        <thead>
                                            <tr>
                                                <th style={{ whiteSpace: 'nowrap' }}>Id</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>Class</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>VIN</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>Year</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>Make</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>Model</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>Adjusted Capital Cost</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>Type</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data?.Transaction?.TransactionAssets?.map((item, index) => (
                                                <tr key={index}>
                                                    <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{item?.Asset?.Id}</td>
                                                    <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{item?.Asset?.AssetClass?.Name}</td>
                                                    <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{item?.Asset?.Vin}</td>
                                                    <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{item?.Asset?.Year}</td>
                                                    <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{item?.Asset?.Make}</td>
                                                    <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{item?.Asset?.Model}</td>
                                                    <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{formatCurrency(item?.AdjustedCapitalCost)}</td>
                                                    <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{item?.TransactionAssetType?.Description}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                ) : (<p>None</p>)}
                            </Row>
                            <br />
                            <b>Lease Details</b>
                            <Row className='mt-1'>
                                {data?.Transaction?.Lease ? (
                                    <Table striped bordered hover responsive>
                                        <thead>
                                            <tr>
                                                <th style={{ whiteSpace: 'nowrap' }}>Id</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>Lease Number</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>Start Date</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>Duration (Months)</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>Address</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>Address2</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>City</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>State</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>Zip</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{data?.Transaction?.Lease?.Id}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{data?.Transaction?.Lease?.LeaseNumber}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{formatDateToMMDDYYYY(data?.Transaction?.Lease?.StartDate)}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{data?.Transaction?.Lease?.DurationMonths}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{data?.Transaction?.Lease?.Address?.AddressLine1}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{data?.Transaction?.Lease?.Address?.AddressLine2}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{data?.Transaction?.Lease?.Address?.City}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{data?.Transaction?.Lease?.Address?.State}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{data?.Transaction?.Lease?.Address?.PostalCode}</td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                ) : (<p>None</p>)}
                            </Row>
                            <br />
                            <b>Customer(s)</b>
                            <Row className='mt-1'>
                                {data?.Transaction?.TransactionCustomerRoles?.length > 0 ? (
                                    <Table striped bordered hover responsive>
                                        <thead>
                                            <tr>
                                                <th style={{ whiteSpace: 'nowrap' }}>Id</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>Name</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>Address</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>Address2</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>City</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>State</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>Zip</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>Role</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data?.Transaction?.TransactionCustomerRoles?.map((d, idx) => (
                                                <tr key={idx}>
                                                    <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{d?.Customer?.Id}</td>
                                                    <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{d?.Customer?.Name}</td>
                                                    <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{d?.Customer?.Address?.AddressLine1}</td>
                                                    <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{d?.Customer?.Address?.AddressLine2}</td>
                                                    <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{d?.Customer?.Address?.City}</td>
                                                    <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{d?.Customer?.Address?.State}</td>
                                                    <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{d?.Customer?.Address?.PostalCode}</td>
                                                    <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{d?.TransactionCustomerRoleType?.Description}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                ) : (<p>None</p>)}
                            </Row>
                            <br />
                            <b>Lien Holder(s)</b>
                            <Row className='mt-1'>
                                {data?.Transaction?.TransactionLienHolders?.length > 0 ? (
                                    <Table striped bordered hover responsive>
                                        <thead>
                                            <tr>
                                                <th style={{ whiteSpace: 'nowrap' }}>Id</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>Name</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>Address</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>Address2</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>City</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>State</th>
                                                <th style={{ whiteSpace: 'nowrap' }}>Zip</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data?.Transaction?.TransactionLienHolders?.map((d, idx) => (
                                                <tr key={idx}>
                                                    <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{d?.LienHolder?.Id}</td>
                                                    <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{d?.LienHolder?.Name}</td>
                                                    <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{d?.LienHolder?.Address?.AddressLine1}</td>
                                                    <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{d?.LienHolder?.Address?.AddressLine2}</td>
                                                    <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{d?.LienHolder?.Address?.City}</td>
                                                    <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{d?.LienHolder?.Address?.State}</td>
                                                    <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{d?.LienHolder?.Address?.PostalCode}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                ) : (<p>None</p>)}
                            </Row>
                            <br />

                            <SeperatorLine />

                            <Row className="align-items-end">
                                <Col></Col>
                                <Col xs="auto">
                                    <Table borderless className='mb-0'>
                                        <tbody>
                                            <tr>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', textAlign: 'right' }} colSpan={2}><b>Pricing Summary</b></td>
                                            </tr>
                                            <tr>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', textAlign: 'right' }}>Final sale price to be used</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', textAlign: 'right' }}>{formatCurrency(data?.FinalSalePrice)}</td>
                                            </tr>
                                            <tr>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', textAlign: 'right' }}>Retired adjusted capital cost</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', textAlign: 'right' }}>{formatCurrency(data?.AdjustedCapitalCost)}</td>
                                            </tr>
                                            <tr>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', textAlign: 'right' }}>Less total transaction value from above</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', textAlign: 'right' }}>-{formatCurrency(data?.AdjustedCapitalCost)}</td>
                                            </tr>
                                            <tr>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', textAlign: 'right', color: data?.TotalTaxableAmount > 0 ? 'red' : 'black' }}><b>Total taxable amount</b></td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', textAlign: 'right', color: data?.TotalTaxableAmount > 0 ? 'red' : 'black' }}><b>{formatCurrency(data?.TotalTaxableAmount)}</b></td>
                                            </tr>
                                            <tr style={{ display: data?.OverUnder < 0 ? 'table-row' : 'none' }}>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', textAlign: 'right', color: 'red' }}><b>Unused Credits</b></td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', textAlign: 'right', color: 'red' }}><b>{formatCurrency(data?.OverUnder)}</b></td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </Col>
                            </Row>

                            {transactionStatus !== 0 && (
                                <Formik
                                    validationSchema={schema}
                                    onSubmit={async (form) => {
                                        form.transactionStatus = transactionStatus;
                                        setLoading(true);
                                        const res = await updateTransactionStatus(transaction.Id, form)
                                        if (res) {
                                            setData(res)
                                            setError(false);
                                            setIsSaved(true);
                                        }
                                        else {
                                            setError(true);
                                        }
                                        setLoading(false);


                                        if (res)
                                            setTimeout(() => closeModal(true), 2000);
                                    }}
                                    initialValues={{
                                        confirmCheckbox: false,
                                    }}
                                >
                                    {({ handleSubmit, handleChange, values, touched, errors }) => (
                                        <Form onSubmit={handleSubmit}>
                                            <Row>
                                                <Col className="d-flex flex-column justify-content-end">
                                                    <Form.Check type="checkbox" name='confirmCheckbox' id="confirmCheckbox" label={<span className="fw-bold"><span style={{ color: 'red' }}>*</span>{confirmationText}</span>} checked={values.confirmCheckbox} onChange={handleChange} isInvalid={touched.confirmCheckbox && !!errors.confirmCheckbox} />
                                                    {touched.confirmCheckbox && errors.confirmCheckbox && (
                                                        <div className="invalid-feedback d-block">{errors.confirmCheckbox}</div>
                                                    )}
                                                </Col>
                                                <Col xs="auto" className="justify-content-end gap-3 mt-3">
                                                    <Button variant={transactionStatus === 4 ? 'danger' : 'primary'} type="submit">{buttonText}</Button>
                                                </Col>
                                            </Row>
                                        </Form>
                                    )}
                                </Formik>
                            )}
                        </div>
                    </Modal.Body>
                ))}
        </Modal>
    );
};

export default ViewTransactionModal;